import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { X, CalendarCheck } from "@phosphor-icons/react";

const FIELDS = [["emission_date", "Emisión"], ["validity_date", "Vigencia"], ["expiration_date", "Vencimiento"]];

export default function BulkDatesModal({ ids, onClose, onDone }) {
  const [f, setF] = useState({ emission_date: "", validity_date: "", expiration_date: "" });
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (!FIELDS.some(([k]) => f[k])) { toast.error("Indica al menos una fecha"); return; }
    if (f.emission_date && f.expiration_date && f.expiration_date < f.emission_date) { toast.error("El vencimiento no puede ser anterior a la emisión"); return; }
    setBusy(true);
    try {
      const body = { ids, ...Object.fromEntries(FIELDS.filter(([k]) => f[k]).map(([k]) => [k, f[k]])) };
      const r = await api.post("/documents/bulk-dates", body);
      toast.success(`${r.data.updated} documento(s) actualizados`);
      if (r.data.skipped.length) { setSkipped(r.data.skipped); onDone(false); }
      else onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al actualizar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-dates-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200 shadow-xl anim-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-5 py-4 border-b border-neutral-200">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><CalendarCheck size={18} weight="duotone" className="text-blue-600"/> Editar fechas · {ids.length} documento(s)</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="bulk-dates-close"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-neutral-500">Solo se modifican los campos que llenes. Aplica a documentos pendientes o rechazados; los aprobados se omiten.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {FIELDS.map(([k, l]) => (
              <label key={k} className="block">
                <span className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-1.5">{l}</span>
                <input type="date" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  className="w-full px-2.5 py-2 border border-neutral-200 rounded-md text-sm" data-testid={`bulk-${k}`}/>
              </label>
            ))}
          </div>
          {skipped && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs text-amber-900 max-h-40 overflow-y-auto" data-testid="bulk-dates-skipped">
              <div className="font-semibold mb-1">{skipped.length} documento(s) omitidos:</div>
              <ul className="list-disc pl-4 space-y-0.5">{skipped.map(s => <li key={s.id}>{s.name || s.id} — {s.reason}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-neutral-200">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded-md text-sm" data-testid="bulk-dates-cancel">{skipped ? "Cerrar" : "Cancelar"}</button>
          {!skipped && (
            <button onClick={submit} disabled={busy} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold disabled:opacity-50" data-testid="bulk-dates-submit">
              {busy ? "Guardando…" : "Aplicar fechas"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
